import { useState, useEffect } from 'react';
import { api, type TagsResponse } from '@/lib/api';
import { FALLBACK_TAGS } from '@/types/index';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { X, Plus, Tag, Crown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useSubscription } from '@/hooks/useSubscription';

const CUSTOM_TAG_LIMIT_FREE = 5;
const CUSTOM_TAG_LIMIT_PREMIUM = 50;

interface TagEditorProps {
  selectedTags: string[];
  onTagsChange: (tags: string[]) => void;
  aiSuggestions?: string[];
  className?: string;
}

export default function TagEditor({ selectedTags, onTagsChange, aiSuggestions, className }: TagEditorProps) {
  const [open, setOpen] = useState(false);
  const [tagData, setTagData] = useState<TagsResponse | null>(null);
  const [newTag, setNewTag] = useState('');
  const [limitError, setLimitError] = useState('');
  const { isPremium } = useSubscription();

  useEffect(() => {
    if (open && !tagData) {
      api.getTags().then(setTagData).catch(() => {});
    }
  }, [open, tagData]);

  const toggleTag = (tag: string) => {
    if (selectedTags.includes(tag)) {
      onTagsChange(selectedTags.filter(t => t !== tag));
    } else {
      onTagsChange([...selectedTags, tag]);
    }
  };

  const customCount = tagData?.customCount ?? 0;
  const limit = isPremium ? CUSTOM_TAG_LIMIT_PREMIUM : CUSTOM_TAG_LIMIT_FREE;
  const canAddMore = customCount < limit;

  const addCustomTag = async () => {
    const normalized = newTag.trim().toLowerCase().replace(/\s+/g, '-');
    if (!normalized) return;
    setLimitError('');

    if (!canAddMore) {
      setLimitError(isPremium
        ? `カスタムタグは${CUSTOM_TAG_LIMIT_PREMIUM}個までです`
        : `無料プランではカスタムタグは${CUSTOM_TAG_LIMIT_FREE}個までです。プレミアムで${CUSTOM_TAG_LIMIT_PREMIUM}個まで追加できます`
      );
      return;
    }

    try {
      await api.createCustomTag(normalized);
      if (tagData) {
        setTagData({ ...tagData, custom: [...tagData.custom, normalized], customCount: customCount + 1 });
      }
      if (!selectedTags.includes(normalized)) {
        onTagsChange([...selectedTags, normalized]);
      }
      setNewTag('');
    } catch {
      // Still add locally
      if (!selectedTags.includes(normalized)) {
        onTagsChange([...selectedTags, normalized]);
      }
      setNewTag('');
    }
  };

  const predefined = tagData?.predefined || FALLBACK_TAGS;
  const custom = tagData?.custom || [];
  const recent = tagData?.recent || [];

  return (
    <div className={cn("space-y-2", className)}>
      {/* Selected tags */}
      <div className="flex flex-wrap gap-1.5">
        {selectedTags.map(tag => (
          <Badge key={tag} variant="default" className="gap-1 cursor-pointer" onClick={() => toggleTag(tag)}>
            {tag}
            <X className="h-3 w-3" />
          </Badge>
        ))}
        <Button variant="outline" size="sm" className="h-6 gap-1 text-xs" onClick={() => setOpen(true)}>
          <Plus className="h-3 w-3" />
          タグを追加
        </Button>
      </div>

      {/* Tag picker dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Tag className="h-5 w-5" />
              タグを選択
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 overflow-y-auto flex-1 min-h-0 -mx-6 px-6">
            {/* AI Suggestions */}
            {aiSuggestions && aiSuggestions.length > 0 && (
              <TagSection
                title="AI提案"
                emoji="🤖"
                tags={aiSuggestions}
                selectedTags={selectedTags}
                onToggle={toggleTag}
              />
            )}

            {/* Recent */}
            {recent.length > 0 && (
              <TagSection
                title="最近使用"
                emoji="🕐"
                tags={recent}
                selectedTags={selectedTags}
                onToggle={toggleTag}
              />
            )}

            {/* Predefined */}
            <TagSection
              title="定義済み"
              emoji="📋"
              tags={predefined}
              selectedTags={selectedTags}
              onToggle={toggleTag}
            />

            {/* Custom */}
            {custom.length > 0 && (
              <TagSection
                title="カスタム"
                emoji="🔧"
                tags={custom}
                selectedTags={selectedTags}
                onToggle={toggleTag}
              />
            )}

            {/* Create new tag */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-medium">新しいタグを作成</h4>
                <span className="text-xs text-muted-foreground">
                  {customCount}/{limit}
                </span>
              </div>
              <div className="flex gap-2">
                <Input
                  placeholder="タグ名..."
                  value={newTag}
                  onChange={e => { setNewTag(e.target.value); setLimitError(''); }}
                  onKeyDown={e => e.key === 'Enter' && addCustomTag()}
                />
                <Button size="sm" onClick={addCustomTag} disabled={!newTag.trim()}>
                  追加
                </Button>
              </div>
              {limitError && (
                <p className="text-xs text-destructive flex items-center gap-1">
                  {!isPremium && <Crown className="h-3 w-3" />}
                  {limitError}
                </p>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TagSection({
  title,
  emoji,
  tags,
  selectedTags,
  onToggle,
}: {
  title: string;
  emoji: string;
  tags: string[];
  selectedTags: string[];
  onToggle: (tag: string) => void;
}) {
  return (
    <div className="space-y-2">
      <h4 className="text-sm font-medium">
        {emoji} {title}
      </h4>
      <div className="flex flex-wrap gap-1.5">
        {tags.map(tag => (
          <Badge
            key={tag}
            variant={selectedTags.includes(tag) ? "default" : "outline"}
            className="cursor-pointer"
            onClick={() => onToggle(tag)}
          >
            {tag}
          </Badge>
        ))}
      </div>
    </div>
  );
}
